import styled from 'styled-components';

export const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ContentSectionContainer = styled.div<{ $isCompact?: boolean }>`
  margin: 0 auto;
  padding: ${({ $isCompact }) => ($isCompact ? '1.25rem' : '2rem')};
  max-width: 1400px;
`;

export const ContentSection = styled.div<{ $isCompact?: boolean }>`
  border-radius: ${({ $isCompact, theme }) =>
    $isCompact ? theme.borderRadius.sm : '8px'};
  box-shadow: ${({ $isCompact }) =>
    $isCompact
      ? '0 1px 2px rgba(15, 23, 42, 0.04)'
      : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  border: ${({ $isCompact, theme }) =>
    $isCompact ? `1px solid ${theme.colors.border.light}` : 'none'};
  padding: ${({ $isCompact }) => ($isCompact ? '1.25rem' : '2rem')};
`;

export const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const ToolbarLeft = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const ToolbarRight = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const DocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

export const DocumentCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.light || '#e0e0e0'};
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

export const DocumentCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

export const DocumentIcon = styled.div`
  font-size: 2rem;
  text-align: center;
  margin-bottom: 0.5rem;
`;

export const DocumentActions = styled.div`
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  position: relative;

  ${DocumentCard}:hover & {
    opacity: 1;
  }
`;

export const ActionButton = styled.button`
  background: transparent;
  border: none;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.2s ease;
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.background || '#f5f5f5'};
    border: 1px solid ${({ theme }) => theme.colors.border.light || '#e0e0e0'};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const DocumentTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: ${({ theme }) => theme.colors.text || '#333'};
  overflow: hidden;
`;

export const DocumentMeta = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.5rem;
  line-height: 1.4;
`;

export const DocumentTags = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

export const Tag = styled.span<{
  variant?: 'primary' | 'secondary' | 'warning';
}>`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  background: ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return '#e3f2fd';
      case 'warning':
        return '#fff3e0';
      default:
        return '#f5f5f5';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return '#1976d2';
      case 'warning':
        return '#f57c00';
      default:
        return '#666';
    }
  }};
`;

export const UploadArea = styled.div<{ isDragging?: boolean }>`
  border: 2px dashed ${({ isDragging }) => (isDragging ? '#1976d2' : '#ccc')};
  border-radius: 8px;
  padding: 3rem 2rem;
  text-align: center;
  background: ${({ isDragging }) => (isDragging ? '#f5f5f5' : 'white')};
  transition: all 0.2s ease;
  margin-top: 2rem;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors?.primary || '#1976d2'};
    background: ${({ theme }) => theme.colors?.background || '#f5f5f5'};
  }
`;

export const UploadIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

export const UploadText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

export const UploadSubtext = styled.div`
  font-size: 0.875rem;
  color: #999;
  margin-bottom: 1rem;
`;

export const UploadButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

export const EmptyStateText = styled.div`
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
`;

export const EmptyStateSubtext = styled.div`
  font-size: 0.875rem;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background: ${({ theme }) => theme.colors.background || '#f5f5f5'};
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light || '#ccc'};
  border-radius: 4px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary || '#1976d2'};
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light || '#ccc'};
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary || '#1976d2'};
  }
`;

export const FormActions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const CollectionsSection = styled.div`
  margin-bottom: 2rem;
`;

export const CollectionsTitle = styled.div`
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const CollectionsGrid = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

export const CollectionPill = styled.button<{
  active?: boolean;
  isSmart?: boolean;
}>`
  padding: 0.5rem 1rem;
  border-radius: 20px;
  border: 1px solid
    ${({ active, isSmart }) =>
      active ? '#1976d2' : isSmart ? '#e8f5e8' : '#e0e0e0'};
  background: ${({ active, isSmart }) =>
    active ? '#1976d2' : isSmart ? '#e8f5e8' : 'white'};
  color: ${({ active, isSmart }) =>
    active ? 'white' : isSmart ? '#2e7d32' : '#333'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    border-color: #1976d2;
    background: ${({ active }) => (active ? '#1976d2' : '#f5f5f5')};
  }
`;

export const DocumentTypeLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.5rem;
  font-weight: 500;
`;

export const DocumentVersionLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

export const DocumentFormatLabel = styled.div`
  font-size: 0.75rem;
  color: #999;
  margin-top: 0.25rem;
  text-transform: uppercase;
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light || '#ccc'};
  border-radius: 4px;
  font-size: 1rem;
  background: white;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary || '#1976d2'};
  }
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text || '#333'};
`;

export const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

export const ToolbarContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 1rem;
`;

export const UploadStatusTile = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isCompact }) => ($isCompact ? '0.75rem' : '1rem')};
  padding: ${({ $isCompact }) => ($isCompact ? '0.75rem 1rem' : '1rem 1.5rem')};
  background: ${({ $isCompact, theme }) =>
    $isCompact
      ? `${theme.colors.primary}0a`
      : 'linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%)'};
  border: 1px solid
    ${({ $isCompact, theme }) =>
      $isCompact ? theme.colors.border.light : '#90caf9'};
  border-radius: ${({ $isCompact, theme }) =>
    $isCompact ? theme.borderRadius.sm : '8px'};
  margin-bottom: ${({ $isCompact }) => ($isCompact ? '1rem' : '1.5rem')};
  box-shadow: ${({ $isCompact }) =>
    $isCompact
      ? '0 1px 2px rgba(15, 23, 42, 0.04)'
      : '0 2px 4px rgba(0, 0, 0, 0.05)'};
`;

export const UploadStatusIcon = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $isCompact }) => ($isCompact ? '1.15rem' : '1.5rem')};
  color: ${({ $isCompact, theme }) =>
    $isCompact ? theme.colors.primary : 'inherit'};
  flex-shrink: 0;
`;

export const UploadStatusContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const UploadStatusTitle = styled.div<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.9rem' : '1rem')};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.25rem;
`;

export const UploadStatusMessage = styled.div<{ $isCompact?: boolean }>`
  font-size: ${({ $isCompact }) => ($isCompact ? '0.8rem' : '0.875rem')};
  color: ${({ $isCompact, theme }) =>
    $isCompact ? theme.colors.text.secondary : '#666'};
  line-height: 1.4;
`;

export const UploadProgressBarTrack = styled.div`
  width: 100%;
  height: 6px;
  margin-top: 0.5rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.border.light};
  overflow: hidden;
`;

export const UploadProgressBarFill = styled.div<{ $value: number }>`
  height: 100%;
  width: ${({ $value }) => `${Math.min(100, Math.max(0, $value))}%`};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.2s ease;
`;

export const UploadStatusActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 0.5rem;
`;

export const ModalSectionLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 0.4rem;
`;

export const ModalHintText = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.45;
`;

export const ModalDocumentName = styled.div`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const PendingFileList = styled.ul`
  list-style: none;
  margin: 0.6rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

export const PendingFileRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  font-size: 0.8rem;
`;

export const PendingFileInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  span:first-child {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span:last-child {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 0.72rem;
  }
`;

export const PendingFileRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover:not(:disabled) {
    color: #b91c1c;
    background: #fef2f2;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export const PendingFileError = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem 0.6rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 0.78rem;
  line-height: 1.4;
`;

export const SuggestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 0.5rem;
`;

export const SuggestionRow = styled.div<{ $current?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  border: 1px solid
    ${({ $current, theme }) =>
      $current ? theme.colors.primary : theme.colors.border.light};
  background: ${({ $current, theme }) =>
    $current ? `${theme.colors.primary}0a` : theme.colors.background};
`;

export const SuggestionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SuggestionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

export const SuggestionName = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SuggestionBadge = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`;

export const SuggestionCurrentTag = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

export const SuggestionReason = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 0.2rem;
  line-height: 1.4;
`;

export const SuggestionEmpty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 0.85rem;
`;

export const DeleteList = styled.div`
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 0.4rem 0.75rem;
  background: ${({ theme }) => theme.colors.background};
`;

export const DeleteListRow = styled.div`
  padding: 0.5rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text.primary};

  &:last-child {
    border-bottom: none;
  }
`;
